import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { CountryToken } from '@app/core/env.tokens';
import { buildStaticPaths } from '@app/routes/static-paths';
import { IconComponent } from '@app/components/ui-icon/icon.component';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { StorageService } from '@services/logic/storage.service';

/**
 * localStorage key remembering that the visitor dismissed the notice. Bump
 * the suffix to show a new version of the notice to everyone again.
 */
export const INCIDENT_NOTICE_DISMISSED_KEY =
  'incident_notice_dismissed_2026_11_back_online';

/**
 * Temporary banner for the November 2026 phishing incident, shown under the
 * header on the DK market only (NL was not affected). Browser-only: it appears after hydration so the notice never lands
 * in the SSR HTML that crawlers index. Shown once per browser: closing it is
 * remembered in localStorage. It also stays hidden on the FAQ page it links
 * to.
 */
@Component({
  selector: 'app-incident-notice',
  templateUrl: './incident-notice.component.html',
  styleUrl: './incident-notice.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, TranslatePipe],
})
export class IncidentNoticeComponent {
  private readonly storage = inject(StorageService);

  private readonly isDk = inject(CountryToken) === 'dk';

  /** Plain href, not routerLink: a full load lands on the FAQ, where the banner stays hidden. */
  protected readonly faqUrl = `/${buildStaticPaths(inject(CountryToken)).securityIncident}`;

  protected readonly open = signal(false);

  constructor() {
    afterNextRender(() => {
      if (
        this.isDk &&
        window.location.pathname !== this.faqUrl &&
        !this.storage.get(INCIDENT_NOTICE_DISMISSED_KEY)
      ) {
        this.open.set(true);
      }
    });
  }

  protected dismiss(): void {
    try {
      // ngx-webstorage's `set` calls localStorage.setItem unguarded; a quota
      // or blocked-storage error must not keep the banner open.
      this.storage.save(INCIDENT_NOTICE_DISMISSED_KEY, true);
    } finally {
      this.open.set(false);
    }
  }
}
